import type { EventName, EventPayloads } from "./mixpanel_generated/events";
import { PAGE_EVENTS, type PageKey, type PageEventsMap } from "./mixpanel_generated/page_events";
import { EVENT_PROP_KEYS } from "./mixpanel_generated/event_properties";
import { trackEvent } from "./trackEvent";
import { isAnalyticsDebug } from "./debug";

type UnionToIntersection<U> = (U extends unknown ? (k: U) => void : never) extends (
  k: infer I,
) => void
  ? I
  : never;

// Stub payloads (`Record<string, never>`) become `{}` so they don't collapse the intersection
// to `never` when an element legitimately fires a stub event alongside a non-stub one.
type StubToEmpty<T> = T extends Record<string, never> ? {} : T;

// Union of event names mapped to a given page/element.
type EventsAt<P extends PageKey, K extends keyof PageEventsMap[P]> = PageEventsMap[P][K] extends {
  events: readonly (infer E)[];
}
  ? Extract<E, EventName>
  : never;

// The props an element's `track()` must accept = intersection of every fired event's payload
// (a single object valid for all of them), with stub payloads coerced to `{}`.
type PayloadFor<P extends PageKey, K extends keyof PageEventsMap[P]> = UnionToIntersection<
  StubToEmpty<EventPayloads[EventsAt<P, K>]>
>;

/**
 * Page-scoped tracker. Use ONLY in page-scoped components (route components / single-page
 * features) — never shared widgets — because the page key is fixed per call site.
 *
 * On `track`, the props are projected down to each fired event's own keys before dispatch, so
 * no event receives another event's properties and dev validation stays strict.
 */
export function createPageAnalytics<P extends PageKey>(pageKey: P) {
  return {
    track<K extends keyof PageEventsMap[P]>(elementKey: K, props: PayloadFor<P, K>): void {
      const entry = PAGE_EVENTS[pageKey]?.[elementKey] as unknown as
        | { events: readonly EventName[] }
        | undefined;
      // Page/element removed from the plan but still referenced by orphaned code → no-op instead
      // of throwing on `entry.events`. (Compile-time, `yarn typecheck` flags such call sites.)
      if (!entry) {
        if (isAnalyticsDebug()) {
          console.warn(
            `[analytics] no plan entry for "${String(pageKey)}.${String(elementKey)}" — skipping (removed from plan?)`,
          );
        }
        return;
      }
      const all = props as Record<string, unknown>;
      for (const ev of entry.events) {
        const projected: Record<string, unknown> = {};
        for (const key of EVENT_PROP_KEYS[ev]) {
          if (key in all) projected[key] = all[key];
        }
        // Localized cast: the dynamically-projected object satisfies this event's payload by
        // construction (keys come from EVENT_PROP_KEYS[ev]); TS can't see that statically.
        trackEvent(ev, projected as EventPayloads[typeof ev]);
      }
    },
  };
}
